import { GrupoRelatorios, ListaCarregada, ParamRelatorio, RelatorioAgenda } from '../interfaces/relatorios-agenda.dto';
import { ItemSelecao } from '../modules/shared/lista-selecao/lista-selecao.component';

// Porta das regras do modal genérico do public/app.js do sim-relatorios.

export type Valores = Record<string, string>;
export interface OpcoesCampo { itens: ItemSelecao[]; aviso: string; }

const PREFIXO_FONTE = '/api/reports/';
const TODOS: ItemSelecao = { valor: '', rotulo: 'Todos' };
export const AVISO_SEM_RESULTADO_NA_UNIDADE = 'Nenhum resultado nesta unidade';

export function caminhoDaFonte(source: string): string {
  return source.startsWith(PREFIXO_FONTE) ? source.slice(PREFIXO_FONTE.length) : source.replace(/^\/+/, '');
}

// Todo source de campo dependente já pode vir com "?tipo=..."; dois "?" quebram a query em silêncio.
export function anexarQueryString(source: string, qs: string): string {
  return source.includes('?') ? `${source}&${qs}` : `${source}?${qs}`;
}

export function ehSelecao(p: ParamRelatorio): boolean {
  return p.type === 'select' || p.type === 'select-establishment';
}

/** Campo oculto que recebe o PATIENT_ID achado pelo CPF. */
export function campoDoCpf(p: ParamRelatorio): string | null {
  return p.type === 'cpf-lookup' ? (p.hiddenField || 'PATIENT_ID') : null;
}

function texto(v: unknown): string {
  return v === undefined || v === null ? '' : String(v);
}

export function valoresIniciais(params: ParamRelatorio[]): Valores {
  const v: Valores = {};
  for (const p of params) {
    if (p.type === 'checkbox-list') v[p.name] = '';
    else {
      const inicial = texto(p.defaultValue);
      // No app.js o "0" padrão de um input aparece vazio; o do select fica.
      v[p.name] = !ehSelecao(p) && inicial === '0' ? '' : inicial;
    }
    const oculto = campoDoCpf(p);
    if (oculto) v[oculto] = '';
  }
  return v;
}

export function visivel(p: ParamRelatorio, valores: Valores): boolean {
  const w = p.visibleWhen;
  if (!w) return true;
  const atual = valores[w.field] ?? '';
  if (Array.isArray(w.oneOf)) return w.oneOf.map(String).includes(atual);
  return w.equals !== undefined && atual === String(w.equals);
}

/** URL das opções com os valores de que o campo depende; null enquanto falta algum. */
export function urlDaFonte(p: ParamRelatorio, params: ParamRelatorio[], valores: Valores): string | null {
  if (!p.source) return null;
  if (!p.dependsOn) return p.source;

  if (!Array.isArray(p.dependsOn)) {
    const v = (valores[p.dependsOn] ?? '').trim();
    return v ? anexarQueryString(p.source, `${p.dependsOn}=${encodeURIComponent(v)}`) : null;
  }

  const partes: string[] = [];
  for (const dep of p.dependsOn) {
    const def = params.find(x => x.name === dep);
    const v = valores[dep] ?? '';
    if (p.type === 'checkbox-list') {
      if (v) partes.push(`${dep}=${encodeURIComponent(v)}`);
      else if (def?.required) return null;
      continue;
    }
    // Campo escondido nunca ganha valor: conta como satisfeito, vazio.
    if (def && !visivel(def, valores)) {
      partes.push(`${dep}=`);
      continue;
    }
    if (!v) return null;
    partes.push(`${dep}=${encodeURIComponent(v)}`);
  }
  return partes.length ? anexarQueryString(p.source, partes.join('&')) : p.source;
}

function paraItens(p: ParamRelatorio, dados: any[]): ItemSelecao[] {
  return dados.map(it => ({ valor: texto(it?.[p.valueKey ?? '']), rotulo: texto(it?.[p.displayKey ?? '']) }));
}

export function opcoesDoCampo(p: ParamRelatorio, url: string | null, lista: ListaCarregada | undefined, valores: Valores): OpcoesCampo {
  if (p.options?.length) {
    const itens = p.options.map(o => ({ valor: texto(o.value), rotulo: o.text }));
    return { itens: p.allowClear && !itens.some(i => i.valor === '') ? [TODOS, ...itens] : itens, aviso: '' };
  }
  if (!p.source) return { itens: [], aviso: '' };
  if (url === null) {
    return { itens: [], aviso: Array.isArray(p.dependsOn) ? 'Informe os campos anteriores primeiro...' : 'Informe o campo anterior primeiro...' };
  }
  if (!lista) return { itens: [], aviso: 'Carregando...' };
  const dependeDeUm = !!p.dependsOn && !Array.isArray(p.dependsOn);
  if (lista.falhou) return { itens: [], aviso: dependeDeUm ? 'Erro ao carregar dados' : 'Não foi possível carregar a lista - tente de novo' };

  if (p.filterFrom && lista.itens.length) {
    const { field, key, inList } = p.filterFrom;
    const pai = valores[field] ?? '';
    // inList: a coluna é ",4,7,"; o delimitador nas pontas impede a unidade 1 de casar com a 10.
    const casa = (it: any) => (inList ? texto(it?.[key]).includes(`,${pai},`) : texto(it?.[key]) === pai);
    const filtrados = pai ? lista.itens.filter(casa) : lista.itens;
    return { itens: [TODOS, ...paraItens(p, filtrados)], aviso: filtrados.length ? '' : AVISO_SEM_RESULTADO_NA_UNIDADE };
  }

  const itens = paraItens(p, lista.itens);
  if (dependeDeUm && itens.length === 0) {
    return { itens: [], aviso: `Nenhum ${(p.label || 'item').toLowerCase()} encontrado` };
  }
  if (itens.length === 0 && !p.dependsOn) return { itens: [], aviso: 'Nenhum registro para este filtro' };
  // O carregador de dependsOn único do app.js não oferece "Todos".
  const comTodos = p.allowClear && !dependeDeUm && !itens.some(i => i.valor === '');
  return { itens: comTodos ? [TODOS, ...itens] : itens, aviso: '' };
}

/** Escolha depois de as opções mudarem; enquanto a lista não chega, nada muda. */
export function escolhaAjustada(p: ParamRelatorio, atual: string, opcoes: OpcoesCampo, lista: ListaCarregada | undefined): string {
  // Sem escolha válida o select volta ao "Selecione..." (populateSelectFromOptions do app.js).
  if (p.options?.length) {
    if (opcoes.itens.some(i => i.valor === atual)) return atual;
    const padrao = p.defaultValue == null ? '' : String(p.defaultValue);
    return padrao && opcoes.itens.some(i => i.valor === padrao) ? padrao : '';
  }
  if (!lista) return atual;
  if (opcoes.itens.some(i => i.valor === atual)) return atual;
  const dependeDeUm = !!p.dependsOn && !Array.isArray(p.dependsOn);
  if (dependeDeUm && opcoes.itens.length === 1) return opcoes.itens[0].valor;
  return '';
}

export function sugestoesDoDatalist(p: ParamRelatorio, lista: ListaCarregada | undefined): string[] {
  return (lista?.itens ?? []).map(it => texto(it?.[p.displayKey ?? '']));
}

export function valorDoDatalist(p: ParamRelatorio, digitado: string, lista: ListaCarregada | undefined): string {
  const achado = lista?.itens.find(it => texto(it?.[p.displayKey ?? '']) === digitado);
  if (achado) return texto(achado[p.valueKey ?? '']);
  if (digitado) return digitado;
  return p.defaultValue !== undefined && p.defaultValue !== null ? String(p.defaultValue) : '0';
}

export function valorDoCheckbox(todos: boolean, marcados: string[]): string {
  return todos ? '' : marcados.join(',');
}

export function valorSincronizado(p: ParamRelatorio, valor: string, lista: ListaCarregada | undefined): { campo: string; valor: string } | null {
  if (!p.syncTo || !p.source) return null;
  if (!valor) return { campo: p.syncTo.field, valor: '' };
  const item = lista?.itens.find(it => texto(it?.[p.valueKey ?? '']) === valor);
  return item && item[p.syncTo.key] != null ? { campo: p.syncTo.field, valor: String(item[p.syncTo.key]) } : null;
}

// checkbox-list não bloqueia: "Todos" é o vazio, como na Agenda.
export function faltaObrigatorio(params: ParamRelatorio[], valores: Valores): boolean {
  return params.some(p => p.required && p.type !== 'checkbox-list' && visivel(p, valores) && !(valores[p.name] ?? '').trim());
}

export function montarCorpo(params: ParamRelatorio[], valores: Valores, listaDe: (p: ParamRelatorio) => ListaCarregada | undefined): Record<string, string> {
  const corpo: Record<string, string> = {};
  for (const p of params) {
    const v = valores[p.name] ?? '';
    corpo[p.name] = p.type === 'datalist' ? valorDoDatalist(p, v, listaDe(p)) : v;
    const oculto = campoDoCpf(p);
    if (oculto) corpo[oculto] = valores[oculto] ?? '';
  }
  return corpo;
}

function normalizar(s: string): string {
  return (s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

export function agruparRelatorios(lista: RelatorioAgenda[], busca: string): GrupoRelatorios[] {
  const termo = normalizar(busca);
  const grupos: GrupoRelatorios[] = [];
  for (const r of lista) {
    if (r.schedulerOnly) continue;
    if (termo && !normalizar(r.name).includes(termo) && !r.id.toLowerCase().startsWith(termo)) continue;
    const nome = r.group || 'Outros';
    let g = grupos.find(x => x.nome === nome);
    if (!g) grupos.push((g = { nome, relatorios: [] }));
    g.relatorios.push(r);
  }
  return grupos;
}
